import { Ionicons } from '@expo/vector-icons';
import { Image } from 'expo-image';
import { useLocalSearchParams } from 'expo-router';
import { LinearGradient } from 'expo-linear-gradient';
import React, { useMemo, useState } from 'react';
import { RefreshControl, ScrollView, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import Animated, { FadeInDown, useReducedMotion } from 'react-native-reanimated';

import { useThemePalette } from '@/config/theme';
import { GoalCelebration } from '@/components/match/GoalCelebration';
import { KickoffCountdown } from '@/components/match/KickoffCountdown';
import { LineupsView, type PlayerMark } from '@/components/match/LineupsView';
import { LiveBallPitch } from '@/components/match/LiveBallPitch';
import { LivePredictCard } from '@/components/match/LivePredictCard';
import { MatchReminderButton } from '@/components/match/MatchReminderButton';
import { MatchComparison } from '@/components/match/MatchComparison';
import { MatchH2H } from '@/components/match/MatchH2H';
import { MatchHighlights } from '@/components/match/MatchHighlights';
import { MatchInfoCard } from '@/components/match/MatchInfoCard';
import { MatchNews } from '@/components/match/MatchNews';
import { MatchPickWidget } from '@/components/match/MatchPickWidget';
import { MatchTrends } from '@/components/match/MatchTrends';
import { ModelInsight } from '@/components/match/ModelInsight';
import { PredictWinCoins } from '@/components/match/PredictWinCoins';
import { PlayerRatings } from '@/components/match/PlayerRatings';
import { PotmVoting } from '@/components/match/PotmVoting';
import { PlayerStatSheet } from '@/components/match/PlayerStatSheet';
import { StatsCompare } from '@/components/match/StatsCompare';
import { XgPanel } from '@/components/match/XgPanel';
import { EventTimeline } from '@/components/match/EventTimeline';
import { AnimatedScore } from '@/components/matches/AnimatedScore';
import { TeamCrest } from '@/components/matches/TeamCrest';
import { StandingsTable } from '@/components/leagues/StandingsTable';
import { AppText } from '@/components/ui/AppText';
import { BackHeader } from '@/components/ui/BackHeader';
import { Card } from '@/components/ui/Card';
import { CardBoundary } from '@/components/ui/CardBoundary';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { LiveBadge } from '@/components/ui/LiveBadge';
import { Screen } from '@/components/ui/Screen';
import { Segmented } from '@/components/ui/Segmented';
import { ShareButton } from '@/components/ui/ShareButton';
import { Skeleton } from '@/components/ui/Skeleton';
import { useLiveBallTracker } from '@/features/live/useLiveBallTracker';
import { compactForm, predictMatch, type TeamFormInput } from '@/features/predictions/model';
import {
  useFixture,
  useLiveMatchStats,
  useMatchEvents,
  useMatchRealtime,
  useMatchXg,
  usePlayerCardsByAfIds,
  usePlayerMatchStats,
  useStandings,
} from '@/features/matches/hooks';
import { computeProvisionalStandings } from '@/features/matches/provisionalStandings';
import { useTopPlayers } from '@/features/leagues/hooks';
import type { FixtureWithRefs, PlayerMatchStatWithPlayer } from '@/lib/data/provider';
import {
  injuriesSchema,
  lineupsPayloadSchema,
  parseOrNull,
  teamStatsPayloadSchema,
} from '@/lib/data/schemas';
import { useRefresh } from '@/lib/data/useRefresh';
import { formatKickoffTime, formatMatchDate } from '@/lib/i18n/format';
import { useLocale } from '@/lib/i18n/LocaleProvider';
import { localizeCompetition, localizeRound, localizeTeamName } from '@/lib/i18n/footballNames';

type MatchTab = 'events' | 'lineups' | 'ratings' | 'stats' | 'xg' | 'standings';

function MatchHeader({ fixture }: { fixture: FixtureWithRefs }) {
  const { t } = useTranslation();
  const { numeralOptions, locale } = useLocale();
  const showScore = fixture.status === 'live' || fixture.status === 'finished';
  // Guard the kickoff date: formatMatchDate/formatKickoffTime call Intl, which
  // throws RangeError on an Invalid Date. kickoff_at is normally a valid DB
  // timestamp, but a malformed/empty value would otherwise crash the whole header.
  const kickoff = fixture.kickoff_at ? new Date(fixture.kickoff_at) : null;
  const kickoffValid = kickoff != null && !Number.isNaN(kickoff.getTime());

  return (
    <Card className="mx-4 mb-4 items-center gap-4 overflow-hidden px-4 py-6">
      <LinearGradient
        colors={['rgba(255,255,255,0.05)', 'rgba(255,255,255,0)']}
        start={{ x: 0, y: 0 }}
        end={{ x: 0, y: 1 }}
        style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '55%', pointerEvents: 'none' }}
      />
      {fixture.competition?.logo_url ? (
        <View style={{ position: 'absolute', top: 12, opacity: 0.05, pointerEvents: 'none' }}>
          <Image
            source={{ uri: fixture.competition.logo_url }}
            style={{ width: 160, height: 160 }}
            contentFit="contain"
            cachePolicy="memory-disk"
          />
        </View>
      ) : null}
      <AppText variant="micro" weight="medium">
        {localizeCompetition(fixture.competition?.name, locale)}
        {fixture.round ? `  ·  ${localizeRound(fixture.round, locale)}` : ''}
      </AppText>
      <View className="w-full flex-row items-start">
        <View className="flex-1 items-center gap-2 px-1">
          <TeamCrest
            uri={fixture.home_team?.logo_url}
            name={localizeTeamName(fixture.home_team, locale)}
            size="xl"
          />
          <AppText
            variant="caption"
            weight="semibold"
            numberOfLines={1}
            adjustsFontSizeToFit
            className="text-center text-ink"
          >
            {localizeTeamName(fixture.home_team, locale)}
          </AppText>
        </View>
        <View className="w-32 items-center gap-1.5 pt-3">
          {showScore ? (
            <AnimatedScore
              home={fixture.home_score ?? 0}
              away={fixture.away_score ?? 0}
              emphasized
              celebrate={false}
            />
          ) : (
            <>
              <AppText variant="micro" weight="medium" numberOfLines={1} className="text-center text-muted">
                {kickoffValid ? formatMatchDate(kickoff, numeralOptions) : ''}
              </AppText>
              <View className="rounded-full bg-volt-glass px-3 py-1.5">
                <AppText variant="heading" weight="bold" tabular numberOfLines={1} className="text-volt-bright">
                  {kickoffValid ? formatKickoffTime(kickoff, numeralOptions) : '—'}
                </AppText>
              </View>
            </>
          )}
          {fixture.status === 'live' ? <LiveBadge minute={fixture.minute} /> : null}
          {fixture.status === 'finished' ? (
            <AppText variant="micro" weight="medium">
              {t('common.finished')}
            </AppText>
          ) : null}
          {fixture.status === 'postponed' ? (
            <AppText variant="micro" weight="medium" className="text-draw">
              {t('common.postponed')}
            </AppText>
          ) : null}
        </View>
        <View className="flex-1 items-center gap-2 px-1">
          <TeamCrest
            uri={fixture.away_team?.logo_url}
            name={localizeTeamName(fixture.away_team, locale)}
            size="xl"
          />
          <AppText
            variant="caption"
            weight="semibold"
            numberOfLines={1}
            adjustsFontSizeToFit
            className="text-center text-ink"
          >
            {localizeTeamName(fixture.away_team, locale)}
          </AppText>
        </View>
      </View>
      {fixture.venue ? (
        <AppText variant="micro" className="text-muted">
          {fixture.venue}
        </AppText>
      ) : null}
      <GoalCelebration
        total={(fixture.home_score ?? 0) + (fixture.away_score ?? 0)}
        active={fixture.status === 'live'}
      />
    </Card>
  );
}

export default function MatchDetailScreen() {
  const palette = useThemePalette();
  const { id } = useLocalSearchParams<{ id: string }>();
  const { t } = useTranslation();
  const [tab, setTab] = useState<MatchTab>('events');
  // The player whose match stat sheet is open (tapped in ratings / lineups).
  const [statSheet, setStatSheet] = useState<PlayerMatchStatWithPlayer | null>(null);

  const fixtureQuery = useFixture(id);
  const eventsQuery = useMatchEvents(id);
  useMatchRealtime(id);

  const fixture = fixtureQuery.data;
  const hasPlayed = fixture?.status === 'live' || fixture?.status === 'finished';
  // Lazy-load the heavy tabs: standings (28 KB) only on its own tab or for the
  // pre-match insight; top scorers only for scheduled matches (the comparison).
  // This keeps match-open to fixture+events instead of a 6-query waterfall.
  const standingsQuery = useStandings(
    tab === 'standings' || fixture?.status === 'scheduled' ? fixture?.competition_id : undefined,
    fixture?.season,
  );
  const ratingsQuery = usePlayerMatchStats(id, hasPlayed);
  // Advanced stats (xG + shotmap) only exist once a match is underway/done.
  const xgQuery = useMatchXg(id, hasPlayed);
  // Live snapshot for the xG ticker - polls every 30s only while the match is live.
  const isLive = fixture?.status === 'live';
  const liveStatsQuery = useLiveMatchStats(id, Boolean(isLive));

  // Live ball-tracking pitch: the Bzzoiro event_id (provider_match_id) is set
  // pre-match on the xG stats and also flows through the live snapshot. Subscribe
  // ONLY for a live fixture that actually carries a mapping.
  const providerMatchId =
    xgQuery.data?.stats?.provider_match_id ?? liveStatsQuery.data?.provider_match_id ?? null;
  const liveEventId =
    providerMatchId !== null && providerMatchId !== '' && Number.isFinite(Number(providerMatchId))
      ? Number(providerMatchId)
      : null;
  // Short team labels (3-letter code, else full name) for the live ball-tracker
  // pitch AND the on-device goal alert. Hoisted once so the alert never falls
  // back to the literal "Home"/"Away" and stays in sync with the pitch props.
  const homeShortName = fixture?.home_team?.short_name || fixture?.home_team?.name || undefined;
  const awayShortName = fixture?.away_team?.short_name || fixture?.away_team?.name || undefined;
  const ballTracker = useLiveBallTracker({
    eventId: liveEventId,
    enabled: Boolean(isLive) && liveEventId !== null,
    homeShortName,
    awayShortName,
    fixtureId: id,
  });
  const topPlayersQuery = useTopPlayers(
    fixture?.status === 'scheduled' ? fixture?.competition_id : undefined,
    fixture?.season,
  );

  const lineups = useMemo(
    () => (fixture ? parseOrNull(lineupsPayloadSchema, fixture.lineups) : null),
    [fixture],
  );
  const teamStats = useMemo(
    () => (fixture ? parseOrNull(teamStatsPayloadSchema, fixture.team_stats) : null),
    [fixture],
  );
  const injuries = useMemo(
    () => (fixture ? parseOrNull(injuriesSchema, fixture.injuries) : null),
    [fixture],
  );

  // Collect every af id in the lineup to fetch player photo cards (so the pitch
  // shows photos even before stats exist).
  const lineupAfIds = useMemo(() => {
    const ids = new Set<number>();
    for (const lu of lineups ?? []) {
      for (const entry of [...(lu.startXI ?? []), ...(lu.substitutes ?? [])]) {
        if (typeof entry.player.id === 'number') ids.add(entry.player.id);
      }
    }
    return [...ids];
  }, [lineups]);
  const cardsQuery = usePlayerCardsByAfIds(lineupAfIds, lineupAfIds.length > 0);

  // af id → photo / rating, merged from player cards + (for played matches) stats.
  const overlay = useMemo(() => {
    const photoByAf = new Map<number, string>();
    const ratingByAf = new Map<number, number>();
    const markByAf = new Map<number, PlayerMark>();
    const clubLogoByAf = new Map<number, string>();
    for (const card of cardsQuery.data ?? []) {
      if (card.af_id != null && card.photo_url) photoByAf.set(card.af_id, card.photo_url);
      if (card.af_id != null && card.club_logo_url) clubLogoByAf.set(card.af_id, card.club_logo_url);
    }
    for (const stat of ratingsQuery.data ?? []) {
      const af = stat.player?.af_id;
      if (af == null) continue;
      if (stat.player?.photo_url && !photoByAf.has(af)) photoByAf.set(af, stat.player.photo_url);
      if (stat.rating != null) ratingByAf.set(af, stat.rating);
      const games = (stat.raw_payload as { games?: { captain?: boolean; substitute?: boolean } } | null)
        ?.games;
      markByAf.set(af, {
        goals: stat.goals ?? 0,
        assists: stat.assists ?? 0,
        yellow: stat.yellow ?? 0,
        red: stat.red ?? 0,
        minutes: stat.minutes ?? null,
        captain: games?.captain === true,
        substitute: games?.substitute === true,
      });
    }
    return { photoByAf, ratingByAf, markByAf, clubLogoByAf };
  }, [cardsQuery.data, ratingsQuery.data]);

  // af id → full stat row, so a tapped lineup dot opens that player's stat sheet.
  const statByAf = useMemo(() => {
    const map = new Map<number, PlayerMatchStatWithPlayer>();
    for (const stat of ratingsQuery.data ?? []) {
      if (stat.player?.af_id != null) map.set(stat.player.af_id, stat);
    }
    return map;
  }, [ratingsQuery.data]);

  // The team name for the open sheet's subtitle (home / away side).
  const sheetTeamName = useMemo(() => {
    if (!statSheet || !fixture) return null;
    if (statSheet.team_id === fixture.home_team?.id) return fixture.home_team?.name ?? null;
    if (statSheet.team_id === fixture.away_team?.id) return fixture.away_team?.name ?? null;
    return null;
  }, [statSheet, fixture]);

  const openStatByAf = (afId: number) => {
    const stat = statByAf.get(afId);
    if (stat) setStatSheet(stat);
  };

  const standingByTeam = useMemo(
    () => new Map((standingsQuery.data ?? []).map((row) => [row.team_id, row])),
    [standingsQuery.data],
  );

  // While the match is live, fold its current score into the table so it shows
  // provisional ranks + movement arrows (display-only; the backend writes the
  // real standings once the match finishes).
  const liveStandings = useMemo(
    () =>
      fixture && fixture.status === 'live'
        ? computeProvisionalStandings(fixture, standingsQuery.data ?? [])
        : null,
    [fixture, standingsQuery.data],
  );

  // Per-team live score (each team's perspective) for the coloured chip on its row.
  const liveScoreByTeam = useMemo(() => {
    if (!fixture || fixture.status !== 'live') return undefined;
    const map = new Map<string, { gf: number; ga: number }>();
    const hs = fixture.home_score ?? 0;
    const as = fixture.away_score ?? 0;
    if (fixture.home_team?.id) map.set(fixture.home_team.id, { gf: hs, ga: as });
    if (fixture.away_team?.id) map.set(fixture.away_team.id, { gf: as, ga: hs });
    return map;
  }, [fixture]);

  const insight = useMemo(() => {
    const rows = standingsQuery.data ?? [];
    const homeId = fixture?.home_team?.id;
    const awayId = fixture?.away_team?.id;
    if (rows.length === 0 || !homeId || !awayId) return null;
    const byTeam = new Map(rows.map((row) => [row.team_id, row]));
    const toInput = (teamId: string): TeamFormInput | null => {
      const row = byTeam.get(teamId);
      return row ? { played: row.played, points: row.points, gd: row.gd, form: row.form } : null;
    };
    const homeInput = toInput(homeId);
    const awayInput = toInput(awayId);
    if (!homeInput && !awayInput) return null;
    const ppg = (input: TeamFormInput | null) =>
      input && input.played > 0 ? input.points / input.played : null;
    return {
      probabilities: predictMatch(homeInput, awayInput),
      homeForm: compactForm(byTeam.get(homeId)?.form),
      awayForm: compactForm(byTeam.get(awayId)?.form),
      homePpg: ppg(homeInput),
      awayPpg: ppg(awayInput),
    };
  }, [standingsQuery.data, fixture?.home_team?.id, fixture?.away_team?.id]);

  const reducedMotion = useReducedMotion();
  const { refreshing, onRefresh } = useRefresh([
    () => fixtureQuery.refetch(),
    () => eventsQuery.refetch(),
    () => standingsQuery.refetch(),
    () => ratingsQuery.refetch(),
    () => xgQuery.refetch(),
    () => liveStatsQuery.refetch(),
  ]);

  if (fixtureQuery.isPending) {
    return (
      <Screen>
        <BackHeader />
        <View className="gap-4 px-4">
          <Skeleton className="h-44 w-full" />
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-24 w-full" />
        </View>
      </Screen>
    );
  }

  if (fixtureQuery.isError || !fixture) {
    return (
      <Screen>
        <BackHeader />
        <ErrorState error={fixtureQuery.error} onRetry={() => fixtureQuery.refetch()} />
      </Screen>
    );
  }

  const shareHome = fixture.home_team?.name ?? '?';
  const shareAway = fixture.away_team?.name ?? '?';
  const scoreline =
    fixture.status === 'finished' || fixture.status === 'live'
      ? `${shareHome} ${fixture.home_score ?? 0}-${fixture.away_score ?? 0} ${shareAway}`
      : `${shareHome} ${t('matches.vs')} ${shareAway}`;
  const shareMessage = `${scoreline}${
    fixture.competition?.name ? ` · ${fixture.competition.name}` : ''
  }\n${t('common.viaApp')}`;

  const hasRatings = (ratingsQuery.data ?? []).length > 0;
  // Only surface the xG tab when the data layer has actually synced SOME advanced
  // slice (stats / shots / player ratings) for this fixture - most matches
  // have none. The panel itself then hides each section whose slice is empty.
  const hasXg =
    Boolean(xgQuery.data?.stats) ||
    (xgQuery.data?.shots.length ?? 0) > 0 ||
    (xgQuery.data?.players.length ?? 0) > 0 ||
    // A live snapshot alone is enough to open the tab (for the live ticker),
    // since the heavier advanced data often lags the first minutes.
    (Boolean(isLive) && Boolean(liveStatsQuery.data));
  const tabs: { value: MatchTab; label: string; icon: keyof typeof Ionicons.glyphMap }[] = [
    { value: 'events', label: t('match.events'), icon: 'time-outline' },
    { value: 'lineups', label: t('match.lineups'), icon: 'people-outline' },
    ...(hasPlayed
      ? [{ value: 'ratings' as const, label: t('match.ratings'), icon: 'star-outline' as const }]
      : []),
    { value: 'stats', label: t('match.stats'), icon: 'stats-chart-outline' },
    ...(hasXg
      ? [{ value: 'xg' as const, label: t('match.xgShort'), icon: 'analytics-outline' as const }]
      : []),
    { value: 'standings', label: t('match.standings'), icon: 'podium-outline' },
  ];

  return (
    <Screen>
      <BackHeader
        title={t('match.title')}
        right={<ShareButton message={shareMessage} accessibilityLabel={t('common.share')} />}
      />
      <ScrollView
        contentContainerStyle={{ paddingBottom: 32 }}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
            tintColor={palette.volt}
            colors={[palette.volt]}
            progressBackgroundColor={palette.surface}
          />
        }
      >
        <Animated.View entering={reducedMotion ? undefined : FadeInDown.springify()}>
          <MatchHeader fixture={fixture} />
        </Animated.View>
        {/* Headline live feature: the 365scores-grade ball-tracking pitch. Mounted
            ONLY for a live fixture that carries a Bzzoiro mapping; otherwise the
            hook is disabled and nothing renders. */}
        {isLive && liveEventId !== null ? (
          <LiveBallPitch
            state={ballTracker}
            homeShortName={homeShortName || 'Home'}
            awayShortName={awayShortName || 'Away'}
          />
        ) : null}
        {fixture.status === 'scheduled' ? (
          <KickoffCountdown kickoffAt={fixture.kickoff_at} />
        ) : null}
        {fixture.status === 'scheduled' ? <MatchReminderButton fixture={fixture} /> : null}
        {insight && (fixture.status === 'scheduled' || fixture.status === 'live') ? (
          <ModelInsight
            probabilities={insight.probabilities}
            homeTeam={fixture.home_team}
            awayTeam={fixture.away_team}
            homeForm={insight.homeForm}
            awayForm={insight.awayForm}
            homePpg={insight.homePpg}
            awayPpg={insight.awayPpg}
          />
        ) : null}
        {fixture.status === 'scheduled' ? (
          <MatchComparison
            fixture={fixture}
            homeRow={fixture.home_team ? (standingByTeam.get(fixture.home_team.id) ?? null) : null}
            awayRow={fixture.away_team ? (standingByTeam.get(fixture.away_team.id) ?? null) : null}
            topPlayers={topPlayersQuery.data ?? []}
          />
        ) : null}
        <MatchPickWidget fixture={fixture} />
        {/* Upcoming-only: stake virtual coins on the 1X2 outcome (multipliers).
            Self-hides unless the match has synced odds/multipliers. */}
        {fixture.status === 'scheduled' ? (
          <CardBoundary>
            <PredictWinCoins fixture={fixture} />
          </CardBoundary>
        ) : null}
        {/* Live-only: predict who scores the next goal (+15 coins if right). */}
        {fixture.status === 'live' ? <LivePredictCard fixture={fixture} /> : null}
        {/* Player of the Match voting (live + finished). Self-hides until the
            match has a player pool (stats), so it never shows an empty card. */}
        {hasPlayed ? (
          <CardBoundary>
            <PotmVoting fixture={fixture} stats={ratingsQuery.data ?? []} />
          </CardBoundary>
        ) : null}
        {/* While live, the facts card drops to the very bottom so events and
            live detail own the top of the screen. */}
        {fixture.status !== 'live' ? (
          <CardBoundary>
            <MatchInfoCard fixture={fixture} />
          </CardBoundary>
        ) : null}
        <View className="mb-4">
          <Segmented options={tabs} value={tab} onChange={setTab} />
        </View>

        {tab === 'events' ? (
          eventsQuery.isPending ? (
            <View className="gap-3 px-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-3/4" />
            </View>
          ) : eventsQuery.isError ? (
            <ErrorState error={eventsQuery.error} onRetry={() => eventsQuery.refetch()} />
          ) : (eventsQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="time-outline"
              title={t('match.noEventsTitle')}
              body={t('match.noEventsBody')}
            />
          ) : (
            <EventTimeline events={eventsQuery.data ?? []} fixture={fixture} />
          )
        ) : null}

        {tab === 'lineups' ? (
          lineups && lineups.length > 0 ? (
            <View className="px-4">
              <LineupsView
                lineups={lineups}
                overlay={overlay}
                injuries={injuries ?? undefined}
                onPlayerPress={openStatByAf}
              />
            </View>
          ) : (
            <EmptyState
              icon="people-outline"
              title={t('match.noLineupsTitle')}
              body={t('match.noLineupsBody')}
            />
          )
        ) : null}

        {tab === 'ratings' ? (
          ratingsQuery.isPending ? (
            <View className="gap-3 px-4">
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-40 w-full" />
            </View>
          ) : hasRatings ? (
            <PlayerRatings
              fixture={fixture}
              stats={ratingsQuery.data ?? []}
              onPlayerPress={setStatSheet}
            />
          ) : (
            <EmptyState
              icon="star-outline"
              title={t('match.noRatingsTitle')}
              body={t('match.noRatingsBody')}
            />
          )
        ) : null}

        {tab === 'stats' ? (
          (teamStats && teamStats.length >= 2) ||
          (eventsQuery.data ?? []).some((e) => e.type === 'yellow' || e.type === 'red') ? (
            <Card className="mx-4">
              <StatsCompare
                payload={teamStats ?? []}
                events={eventsQuery.data ?? []}
                homeTeam={fixture.home_team}
                awayTeam={fixture.away_team}
              />
            </Card>
          ) : (
            <EmptyState
              icon="stats-chart-outline"
              title={t('match.noStatsTitle')}
              body={t('match.noStatsBody')}
            />
          )
        ) : null}

        {tab === 'xg' ? (
          hasXg ? (
            <CardBoundary>
              <XgPanel
                fixtureId={fixture.id}
                homeTeam={fixture.home_team}
                awayTeam={fixture.away_team}
                enabled={hasPlayed}
                isLive={Boolean(isLive)}
                isFinished={fixture.status === 'finished'}
              />
            </CardBoundary>
          ) : (
            <EmptyState
              icon="analytics-outline"
              title={t('match.noXgTitle')}
              body={t('match.noXgBody')}
            />
          )
        ) : null}

        {tab === 'standings' ? (
          standingsQuery.isPending ? (
            <View className="gap-3 px-4">
              <Skeleton className="h-48 w-full" />
            </View>
          ) : standingsQuery.isError ? (
            <ErrorState error={standingsQuery.error} onRetry={() => standingsQuery.refetch()} />
          ) : (standingsQuery.data ?? []).length === 0 ? (
            <EmptyState
              icon="podium-outline"
              title={t('match.noStandingsTitle')}
              body={t('match.noStandingsBody')}
            />
          ) : (
            <View className="px-4">
              <StandingsTable
                rows={liveStandings ?? standingsQuery.data ?? []}
                isLive={Boolean(liveStandings)}
                liveScoreByTeam={liveScoreByTeam}
                highlightTeamIds={[fixture.home_team?.id, fixture.away_team?.id].filter(
                  (teamId): teamId is string => Boolean(teamId),
                )}
              />
            </View>
          )
        ) : null}

        {/* Context & editorial: the core match data (tabs above) owns the top of
            the screen; recent form, head-to-head, clips and news sit below it. */}
        <View className="mt-6">
          {/* Form Trends: recent-form insights (365scores), not betting. Self-hides
              when the fixture has no synced trends. */}
          <CardBoundary>
            <MatchTrends fixtureId={fixture.id} />
          </CardBoundary>
          {/* 365scores head-to-head: W-D-L summary + recent previous meetings.
              Self-hides when the fixture has no 365 game mapping / no meetings. */}
          <CardBoundary>
            <MatchH2H fixtureId={fixture.id} />
          </CardBoundary>
          {/* Video rail (press-conference clips pre-game, match clips post-game);
              self-hides when the fixture carries no highlights. */}
          <CardBoundary>
            <MatchHighlights fixtureId={fixture.id} />
          </CardBoundary>
          {/* World Cup news for this match (+ general WC news); self-hides empty. */}
          <CardBoundary>
            <MatchNews fixtureId={fixture.id} />
          </CardBoundary>
        </View>

        {fixture.status === 'live' ? (
          <View className="mt-5">
            <CardBoundary>
              <MatchInfoCard fixture={fixture} />
            </CardBoundary>
          </View>
        ) : null}
      </ScrollView>
      <PlayerStatSheet
        stat={statSheet}
        teamName={sheetTeamName}
        onClose={() => setStatSheet(null)}
      />
    </Screen>
  );
}
